import SelectComponent from '@components/inputs/select/Select.component';
import { Sizes } from '@constants/sizes.constants';
import { Close } from '@icons/index';
import { Button, Divider, Drawer, IconButton, Stack, Typography } from '@mui/material';
import { AuditType } from '@store/audits/audit/audit.model';
import { auditsInformationService } from '@store/audits/audits-information/audit-information.service';
import {
  AuditInformation,
  AuditInformationCreatedFromStepEnum,
  AuditInformationStatusEnum,
  AuditInformationTypeEnum,
  CreateAuditInformation
} from '@store/audits/audits-information/audits-information.model';
import {
  AuditOperation,
  AuditOperationStepTypeEnum,
  AuditOperationTypeEnum,
  CreateAuditOperation
} from '@store/audits/audits-operation/audits-operation.model';
import { auditsOperationService } from '@store/audits/audits-operation/audits-operation.service';
import { FieldValidationType, checkIfErrors, getFieldError } from '@utils/yup.utils';
import { useSnackbar } from 'notistack';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Outlet, useLocation, useNavigate, useParams } from 'react-router-dom';
import { finalize } from 'rxjs';
import PreAuditObservation from '../pre-audit/components/PreAuditObservation';
import AuditContainer from './components/AuditContainer';
import AuditMap from './components/AuditMap';
import {Colors} from '@constants/colors.constants';

interface LocationState {
  auditOperation?: AuditOperation;
  auditInformation?: AuditInformation;
}

const AddAuditDataDrawer = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [dataType, setDataType] = useState(AuditType.CONTAINER);
  const { auditId } = useParams();
  const [loading, setLoading] = useState<boolean>();
  const { enqueueSnackbar } = useSnackbar();

  const [auditOperation, setAuditOperation] = useState<CreateAuditOperation>({
    auditId: 0,
    type: AuditOperationTypeEnum.CLASSIC
  });
  const [auditInfo, setAuditInfo] = useState<CreateAuditInformation>({
    type: AuditInformationTypeEnum.OBSERVATION
  });

  const location: LocationState = useLocation().state as LocationState;
  const currentAuditOperation = location?.auditOperation;
  const currentAuditInformation = location?.auditInformation;
  const handleClose = () => navigate(-1);

  const errorsObservation = {
    observation: getFieldError(auditInfo?.observation?.observation, FieldValidationType.REQUIRED_STRING_LONG),
  };

  const errorsContainer = {
    container: getFieldError(auditOperation?.containerId || auditOperation?.containerCustom, FieldValidationType.REQUIRED_STRING),
    quantity: getFieldError(auditOperation?.quantity, FieldValidationType.REQUIRED_NUMBER),
    by: getFieldError(auditOperation?.by, FieldValidationType.REQUIRED_STRING),
    waste: getFieldError(auditOperation?.wasteId || auditOperation?.wasteCustom, FieldValidationType.REQUIRED_STRING),
    whereIs: getFieldError(auditOperation?.whereIs, FieldValidationType.STRING_LONG),
  };

  const errorsMap = {
    legend: getFieldError(auditInfo?.map?.legend, FieldValidationType.REQUIRED_STRING),
  };

  const getError = (type: any) => {
    if (type === 'CONTAINER') {
      return errorsContainer;
    } else if (type === 'OBSERVATION') {
      return errorsObservation;
    } else {
      return errorsMap;
    }
  };

  const updateAuditOperation = <T extends keyof CreateAuditOperation>(
    field: T,
    value: CreateAuditOperation[T]
  ) => {
    setAuditOperation((prevAuditOperation) => {
      if (field === 'frequency') {
        const prevFrequency = prevAuditOperation.frequency ?? {};
        const updatedFrequency = {
          ...prevFrequency,
          ...(prevFrequency as any),
          ...(value as any)
        };
        return {
          ...prevAuditOperation,
          frequency: updatedFrequency
        };
      } else {
        return {
          ...prevAuditOperation,
          [field]: value
        };
      }
    });
  };

  const updateAuditInformation = (field: keyof CreateAuditInformation, value: any) => {
    setAuditInfo((createAuditInformation) => {
      if (field === 'type') {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { type, ...rest } = createAuditInformation;
        return {
          type: value
        };
      } else {
        return {
          ...createAuditInformation,
          [field]: {
            ...(createAuditInformation[field] || {}),
            ...value,
          },
        };
      }
    });
  };

  function loadAuditInformation(auditInformation: AuditInformation) {

    const { type, status, documents, description } = auditInformation;

    switch (type) {
      case AuditInformationTypeEnum.MAP:
        setDataType(AuditType.MAP);
        updateAuditInformation('type', AuditInformationTypeEnum.MAP);
        updateAuditInformation('map', { legend: description, documents: documents, status: status });
        break;

      case AuditInformationTypeEnum.OBSERVATION:
        setDataType(AuditType.OBSERVATION);
        updateAuditInformation('type', AuditInformationTypeEnum.OBSERVATION);
        updateAuditInformation('observation', { observation: description, photos: documents, status: status });
        break;

      default:
        break;
    }
  }

  function loadAuditOperation(auditOperation: AuditOperation) {
    setDataType(AuditType.CONTAINER);
    updateAuditOperation('type', auditOperation.type);
    updateAuditOperation('containerId', auditOperation.containerId);
    updateAuditOperation('containerCustom', auditOperation.containerCustom);
    updateAuditOperation('container', auditOperation.container);
    updateAuditOperation('quantity', auditOperation.quantity);
    updateAuditOperation('by', auditOperation.ownedBy);
    updateAuditOperation('description', auditOperation.description);
    updateAuditOperation('whereIs', auditOperation.description);
    updateAuditOperation('wasteId', auditOperation.wasteId);
    updateAuditOperation('wasteUnitName', auditOperation.wasteUnitName);
    updateAuditOperation('annualEstimatedWeightInTons', auditOperation.annualEstimatedWeightInTons);
    updateAuditOperation('stepType', auditOperation.stepType);
    updateAuditOperation('waste', auditOperation.waste);
    updateAuditOperation('wasteCustom', auditOperation.wasteCustom);
    updateAuditOperation('schedule', auditOperation.schedule);
    updateAuditOperation('documents', auditOperation.documents);
  }


  useEffect(() => {
    if (auditId) {
      updateAuditOperation('auditId', Number(auditId));
      updateAuditOperation('stepType', AuditOperationStepTypeEnum.AUDIT);
    }
    if (currentAuditOperation) {
      loadAuditOperation(currentAuditOperation);
    }
    if (currentAuditInformation) {
      loadAuditInformation(currentAuditInformation);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auditId, currentAuditOperation, currentAuditInformation]);


  function handleChangeDataType(type: any) {
    setDataType(type);
  }

  function createOrUpdateAuditOperation() {
    setLoading(true);

    if (dataType === AuditType.CONTAINER) {
      if (auditOperation) {
        auditOperation.stepType = AuditOperationStepTypeEnum.AUDIT;
      }
      const createOrUpdate = currentAuditOperation
        ? auditsOperationService.updateAuditOperation(currentAuditOperation.id, auditOperation)
        : auditsOperationService.createAuditOperation(auditOperation);

      createOrUpdate.pipe(finalize(() => setLoading(false)))
        .subscribe({
          next: () => {
            enqueueSnackbar(
              auditOperation.id
                ? t('audits.success.audit.update-container')
                : t('audits.success.audit.create-container'),
              { variant: 'success' }
            );
            handleClose();
          },
          error: (err) => enqueueSnackbar(err.text, err.options),
        });
    } else if (dataType === AuditType.OBSERVATION) {
      if (auditInfo.observation) {
        auditInfo.observation.createdFrom = AuditInformationCreatedFromStepEnum.AUDIT;
      }
      const createOrUpdate = currentAuditInformation
        ? auditsInformationService.updateAuditInformationObservation(auditInfo.observation!, currentAuditInformation.id)
        : auditsInformationService.createAuditInformationObservation(auditInfo.observation!);

      createOrUpdate.pipe(finalize(() => setLoading(false)))
        .subscribe({
          next: () => {
            enqueueSnackbar(
              currentAuditInformation
                ? t('audits.success.audit.update-observation')
                : t('audits.success.audit.create-observation'),
              { variant: 'success' }
            );
            handleClose();
          },
          error: (err) => enqueueSnackbar(err.text, err.options),
        });
    } else if (dataType === AuditType.MAP && auditInfo) {
      if (auditInfo.map) {
        auditInfo.map.createdFrom = AuditInformationCreatedFromStepEnum.AUDIT;
      }
      const createOrUpdate = currentAuditInformation
        ? auditsInformationService.updateAuditInformationMap(auditInfo.map!, currentAuditInformation.id)
        : auditsInformationService.createAuditInformationMap(auditInfo.map!);

      createOrUpdate.pipe(finalize(() => setLoading(false)))
        .subscribe({
          next: () => {
            enqueueSnackbar(
              currentAuditInformation
                ? t('audits.success.audit.update-map')
                : t('audits.success.audit.create-map'),
              { variant: 'success' }
            );
            handleClose();
          },
          error: (err) => enqueueSnackbar(err.text, err.options),
        });
    }
  }

  const getStringType = (type: AuditInformationTypeEnum) => {
    switch (type) {
      case AuditInformationTypeEnum.CONTACT:
        return 'contact';
      case AuditInformationTypeEnum.DATA:
        return 'data';
      case AuditInformationTypeEnum.NEED:
        return 'need';
      case AuditInformationTypeEnum.OBSERVATION:
        return 'observation';
      case AuditInformationTypeEnum.MAP:
        return 'map';
      default:
        return 'contact';
    }
  };

  const getStatusByType = (type: AuditInformationTypeEnum) => {
    switch (type) {
      case AuditInformationTypeEnum.CONTACT:
        return auditInfo?.contact?.status;
      case AuditInformationTypeEnum.DATA:
        return auditInfo?.data?.status;
      case AuditInformationTypeEnum.NEED:
        return auditInfo?.need?.status;
      case AuditInformationTypeEnum.OBSERVATION:
        return auditInfo?.observation?.status;
      case AuditInformationTypeEnum.MAP:
        return auditInfo?.map?.status;
      default:
        return auditInfo?.contact?.status;
    }
  };


  return (
    <>
      <Drawer anchor="right" open onClose={handleClose}>
        <Stack overflow="hidden" height="100%">
          <Stack direction="row" justifyContent="flex-end">
            <IconButton onClick={handleClose}>
              <Close />
            </IconButton>
          </Stack>
          <Stack pr="10px" py="15px" flexGrow={1} display="flex" flexDirection="column" className='scrollable' overflow='auto'>
            <Stack direction="row" alignItems="center" mb="15px" spacing={1.5}>
              <Typography fontSize={24} fontWeight="700">
                {currentAuditOperation || currentAuditInformation ? t('audits.preAudit.updateData') : t('audits.preAudit.addData')}
              </Typography>
            </Stack>
            {currentAuditInformation?.author && (
                <Typography fontSize={12} fontWeight="400" mb="3px" color={Colors.textWithOpacity}>
                  {t('global.createdBy', {by: {firstName: currentAuditInformation?.author?.firstName, lastName: currentAuditInformation?.author?.lastName}})}
                </Typography>
            )}
            {currentAuditOperation?.author && (
                <Typography fontSize={12} fontWeight="400" mb="3px" color={Colors.textWithOpacity}>
                  {t('global.createdBy', {by: {firstName: currentAuditOperation?.author?.firstName, lastName: currentAuditOperation?.author?.lastName}})}
                </Typography>
            )}
            <Divider variant="fullWidth" orientation="horizontal" />
            <Stack py="24px" pr="10px" spacing={1}>
              <SelectComponent
                color="primary"
                labelWidth={Sizes.drawerInputLabelWidth}
                label={t('audits.preAudit.type')}
                placeholder={t('global.select')}
                handleChange={handleChangeDataType}
                items={AuditType.selectItems()}
                value={dataType}
              />
              {currentAuditInformation && (
                <SelectComponent
                  color="primary"
                  labelWidth={Sizes.drawerInputLabelWidth}
                  label={t('audits.preAudit.status')}
                  placeholder={t('global.select')}
                  handleChange={(value) => { updateAuditInformation(getStringType(auditInfo?.type ?? AuditInformationTypeEnum.CONTACT), { status: value }); }}
                  items={AuditInformationStatusEnum.selectItems()}
                  value={getStatusByType(auditInfo?.type) ?? AuditInformationStatusEnum.PENDING}
                />
              )}
            </Stack>
            <Divider variant="fullWidth" orientation="horizontal" />
            {[
              { type: 'CONTAINER', component: <AuditContainer errors={errorsContainer} auditOperation={auditOperation} updateAuditOperation={updateAuditOperation} /> },
              { type: 'OBSERVATION', component: <PreAuditObservation errors={errorsObservation} auditInformation={auditInfo} updateAuditInformation={updateAuditInformation} /> },
              { type: 'MAP', component: <AuditMap errors={errorsMap} auditInformation={auditInfo} updateAuditInformation={updateAuditInformation} /> }
            ].map(item => dataType === item.type && item.component)}
            <Stack flexGrow={1} />
            <Stack mt="3px" direction="row" spacing={2} justifyContent="center" mb={1}>
              <Button variant="contained" color="primary" onClick={createOrUpdateAuditOperation} disabled={loading || checkIfErrors(getError(dataType))}>
                {currentAuditOperation || currentAuditInformation ? 'Modifier' : t('global.add')}
              </Button>
            </Stack>
          </Stack>
        </Stack>
      </Drawer>
      <Outlet />
    </>
  );
};

export default AddAuditDataDrawer;
